// ─────────────────────────────────────────────────────────────────────────────
//  Classified advertising-fee checkout — Stripe Payment Element for the
//  ClassifiedMonthlyFee charged at the time of posting. Mirrors BazaarCheckoutPage.tsx:
//  reached from CreateListingPage's classified-ad submit, which creates the listing
//  (at PendingPayment) first and hands the resulting clientSecret here via router state.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, classifiedService } from '../services/api';
import { ListingStatus } from '../types';

function CheckoutForm({ listingId, feeAmount }: { listingId: string; feeAmount: number }) {
  const { getToken } = useAppAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message ?? 'Payment failed. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      await classifiedService(createApiClient(getToken)).confirmPayment(listingId, paymentIntent!.id);
      navigate('/classifieds');
    } catch (err: any) {
      setError(err?.message ?? 'Payment succeeded but we could not confirm your listing — contact support.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}
      <button
        data-testid="classified-checkout-pay-button"
        type="submit"
        disabled={!stripe || submitting}
        className="w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? 'Processing…' : `Pay £${feeAmount.toFixed(2)} advertising fee`}
      </button>
    </form>
  );
}

export function ClassifiedCheckoutPage() {
  const { listingId } = useParams<{ listingId: string }>();
  const location = useLocation() as { state?: { clientSecret?: string; feeAmount?: number } };
  const { getToken, account } = useAppAuth();
  const [clientSecret] = useState<string | undefined>(location.state?.clientSecret);
  const [feeAmount] = useState<number>(location.state?.feeAmount ?? 0);
  const [stripePromise] = useState(() =>
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
      ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
      : null
  );

  const { data: listing } = useQuery({
    queryKey: ['classified', listingId],
    queryFn: () => classifiedService(createApiClient(getToken)).get(listingId!),
    enabled: !!listingId && !!account,
  });

  useEffect(() => {
    document.title = 'Checkout — TransitDeal';
  }, []);

  if (!account) return null;

  return (
    <div data-testid="classified-checkout-screen" className="page-shell-tight">
      <Link to="/classifieds" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Classifieds
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Advertising Fee</h1>

      {listing && listing.status !== ListingStatus.PendingPayment ? (
        <div data-testid="classified-checkout-already-paid" className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4 text-sm font-semibold">
          <CheckCircle className="w-4 h-4" /> This listing's advertising fee has already been paid.
        </div>
      ) : clientSecret && stripePromise ? (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm listingId={listingId!} feeAmount={feeAmount} />
          </Elements>
        </div>
      ) : (
        <div data-testid="classified-checkout-session-expired" className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-xl p-4">
          This checkout session is no longer available in this tab. Please return to My Listings and complete
          payment for this ad from there.
        </div>
      )}
    </div>
  );
}
